import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import projectKitchen from "@/assets/project-kitchen.jpg";
import projectBedroom from "@/assets/project-bedroom.jpg";
import projectBathroom from "@/assets/project-bathroom.jpg";
import projectLiving from "@/assets/project-living.jpg";
import portfolioPaint from "@/assets/portfolio-paint.jpg";
import portfolioOffice from "@/assets/portfolio-office.jpg";

const categories = ["الكل", "تصميم داخلي", "ترميم", "دهانات", "مقاولات"];

const projects = [
  { img: projectLiving, title: "صالة استقبال فاخرة", category: "تصميم داخلي" },
  { img: projectKitchen, title: "مطبخ عصري متكامل", category: "ترميم" },
  { img: projectBedroom, title: "جناح رئيسي أنيق", category: "تصميم داخلي" },
  { img: projectBathroom, title: "حمام رخامي فاخر", category: "ترميم" },
  { img: portfolioPaint, title: "دهانات وورق جدران فاخر", category: "دهانات" },
  { img: portfolioOffice, title: "تصميم مكتب تجاري", category: "مقاولات" },
];

const ProjectsSection = () => {
  const [activeCategory, setActiveCategory] = useState("الكل");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = activeCategory === "الكل"
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  const openLightbox = (i: number) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);

  const navigate = (dir: number) => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + dir + filtered.length) % filtered.length);
  };

  return (
    <section id="projects" className="section-padding bg-muted">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">معرض أعمالنا</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            نماذج حية من مشاريعنا المنفذة بأعلى معايير الإتقان والجودة في الرياض
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12 px-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                activeCategory === cat
                  ? "gold-gradient text-primary-foreground gold-shadow"
                  : "bg-card border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((project, i) => (
              <motion.div
                key={project.title}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group relative overflow-hidden rounded-xl aspect-[4/3] cursor-pointer"
                onClick={() => openLightbox(i)}
              >
                <img
                  src={project.img}
                  alt={project.title}
                  loading="lazy"
                  width={800}
                  height={600}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                  <span className="text-primary text-sm font-medium mb-1">{project.category}</span>
                  <h3 className="text-xl font-bold text-marble">{project.title}</h3>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-charcoal/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 left-6 text-marble/70 hover:text-marble transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); navigate(1); }}
              className="absolute right-4 md:right-8 text-marble/70 hover:text-marble transition-colors z-10"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              className="absolute left-4 md:left-8 text-marble/70 hover:text-marble transition-colors z-10"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[85vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filtered[lightboxIndex].img}
                alt={filtered[lightboxIndex].title}
                className="w-full h-full object-contain rounded-lg"
              />
              <div className="text-center mt-4">
                <span className="text-primary text-sm font-medium">{filtered[lightboxIndex].category}</span>
                <h3 className="text-xl font-bold text-marble mt-1">{filtered[lightboxIndex].title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectsSection;
